import React, { useState, useEffect, useRef } from 'react';
import {
  motion,
  AnimatePresence,
  MotionConfig,
  useInView,
} from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Mic,
  Database,
  MessageCircle,
  Check,
  Star,
  ChevronRight,
} from 'lucide-react';
import LandingNavbar from '../components/LandingNavbar';
import LogoIcon from '../components/LogoIcon';

/* ------------------------------------------------------------------ *
 *  Motion Primitives
 * ------------------------------------------------------------------ */

const EASE = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

const Reveal = ({ children, delay = 0, y = 24, className = '' }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.7, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

const Counter = ({ to, suffix = '', prefix = '', decimals = 0, duration = 1.6 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(to * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to, duration]);

  return (
    <span ref={ref}>
      {prefix}
      {val.toFixed(decimals)}
      {suffix}
    </span>
  );
};

/* ------------------------------------------------------------------ *
 *  Live 60 FPS HTML5 Canvas Animated Tech Neural Background
 * ------------------------------------------------------------------ */
const AnimatedTechBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    const resize = () => {
      if (canvas && canvas.parentElement) {
        canvas.width = canvas.parentElement.offsetWidth;
        canvas.height = canvas.parentElement.offsetHeight;
      }
    };
    resize();
    window.addEventListener('resize', resize);

    const numParticles = 65;
    const particles = Array.from({ length: numParticles }, () => ({
      x: Math.random() * (canvas.width || 1200),
      y: Math.random() * (canvas.height || 700),
      vx: (Math.random() - 0.5) * 0.9,
      vy: (Math.random() - 0.5) * 0.9,
      radius: Math.random() * 2.5 + 1.2,
    }));

    const render = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist < 160) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(148, 163, 184, ${0.4 * (1 - dist / 160)})`;
            ctx.lineWidth = 1.2;
            ctx.stroke();
          }
        }
      }

      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(148, 163, 184, 0.85)';
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full object-cover pointer-events-none opacity-50 dark:opacity-75"
    />
  );
};

/* ------------------------------------------------------------------ *
 *  Interactive 3D Cybernetic Grid & Neural Synapse Animation
 * ------------------------------------------------------------------ */
const Cybernetic3DGridBackground = () => (
  <div className="absolute inset-0 -z-10 pointer-events-none overflow-hidden bg-white dark:bg-[#111827]">
    {/* Futuristic AI Texture */}
    <img
      src="/assets/images/hero_futuristic_bg.jpg"
      alt="Futuristic AI Background"
      className="w-full h-full object-cover opacity-20 dark:opacity-30 scale-105"
    />

    {/* 3D Moving Perspective Cyber Grid */}
    <div
      className="absolute inset-0 opacity-25 dark:opacity-40"
      style={{
        perspective: '800px',
        transformStyle: 'preserve-3d',
      }}
    >
      <div
        className="absolute inset-x-[-50%] top-[-20%] bottom-[-50%] bg-[linear-gradient(to_right,rgba(148,163,184,0.18)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.18)_1px,transparent_1px)] bg-[size:48px_48px]"
        style={{
          transform: 'rotateX(60deg) translateY(0px)',
          animation: 'cyberGridScroll 18s linear infinite',
        }}
      />
    </div>

    {/* Dynamic 60 FPS Particle Canvas */}
    <AnimatedTechBackground />

    {/* Vignette Mask */}
    <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-white/40 to-white dark:from-[#111827]/80 dark:via-[#111827]/55 dark:to-[#111827]" />

    <style>{`
      @keyframes cyberGridScroll {
        0% { transform: rotateX(60deg) translateY(0px); }
        100% { transform: rotateX(60deg) translateY(48px); }
      }
    `}</style>
  </div>
);

/* ------------------------------------------------------------------ *
 *  Hero Section with Full-Width Dashboard Screenshot
 * ------------------------------------------------------------------ */

const Hero = () => {
  const navigate = useNavigate();

  return (
    <section className="relative z-10 pt-36 pb-28 px-6 lg:px-12 bg-white dark:bg-[#111827] border-b border-[#E5E7EB] dark:border-slate-800 overflow-hidden">
      {/* 3D Cybernetic Moving Grid & Neural Background */}
      <Cybernetic3DGridBackground />

      <div className="mx-auto max-w-7xl relative z-10">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center text-center max-w-4xl mx-auto"
        >
          <motion.div variants={fadeUp}>
            <div className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-[#F9FAFB] px-4 py-1.5 shadow-2xs mb-6">
              <span className="h-2 w-2 rounded-full bg-[#111827]" />
              <span className="text-xs font-semibold text-[#374151]">
                Next-Gen AI Digital Twin Platform
              </span>
            </div>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-4xl font-bold tracking-tight text-[#111827] sm:text-6xl lg:text-7xl leading-[1.08]"
          >
            Your business, answering 24/7 with a human-like AI Digital Twin.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mt-6 text-base sm:text-xl text-[#4B5563] leading-relaxed max-w-2xl font-normal"
          >
            Clone your voice, ingest your menus and documents, and automate customer conversations on WhatsApp, phone calls, and web chat. Set up in under 5 minutes.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-9 flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <button
              onClick={() => navigate('/register')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl bg-[#111827] hover:bg-[#1F2937] px-8 py-4 text-sm font-semibold text-white shadow-sm transition-all"
            >
              Start Free Trial
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              onClick={() => navigate('/pricing')}
              className="w-full sm:w-auto flex items-center justify-center gap-2 rounded-xl border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] px-8 py-4 text-sm font-semibold text-[#111827] transition-all"
            >
              View Pricing & Plans
            </button>
          </motion.div>

          {/* Social Proof Line */}
          <motion.div variants={fadeUp} className="mt-10 flex items-center gap-4 text-xs text-[#6B7280]">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-[#111827] text-[#111827]" />
              ))}
            </div>
            <span>Trusted by over 1,000+ restaurants, clinics, boutiques & real estate agencies</span>
          </motion.div>
        </motion.div>

        {/* Product Screenshot / Interactive Live Dashboard Preview */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
          className="mt-16 relative mx-auto max-w-6xl"
        >
          <div className="rounded-2xl border border-[#E5E7EB] bg-white shadow-2xl overflow-hidden">
            {/* Mac-style Window Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-[#EF4444]/80" />
                <span className="h-3 w-3 rounded-full bg-[#F59E0B]/80" />
                <span className="h-3 w-3 rounded-full bg-[#10B981]/80" />
              </div>
              <div className="rounded-md bg-white px-5 py-1 text-[11px] font-mono text-[#6B7280] border border-[#E5E7EB] shadow-2xs">
                app.aidigitaltwin.com/dashboard
              </div>
              <div className="w-12" />
            </div>

            {/* Pure React Interactive Dashboard Mockup */}
            <div className="grid grid-cols-1 md:grid-cols-12 text-[#111827] bg-[#FAFAF7]">
              {/* Left Sidebar */}
              <div className="md:col-span-3 border-r border-[#E5E7EB] bg-white p-5 flex flex-col justify-between hidden md:flex">
                <div>
                  {/* Brand Header */}
                  <div className="flex items-center gap-2.5 pb-6 border-b border-[#E5E7EB]">
                    <LogoIcon className="h-7 w-auto text-[#111827]" />
                    <span className="font-bold text-sm tracking-tight text-[#111827]">AI Digital Twin</span>
                  </div>

                  {/* Navigation Links */}
                  <div className="mt-6 space-y-1">
                    {[
                      { name: 'Dashboard', active: true },
                      { name: 'Analytics', active: false },
                      { name: 'Conversations', active: false },
                      { name: 'Knowledge Base', active: false },
                      { name: 'Voice Studio', active: false },
                      { name: 'Settings', active: false },
                    ].map((item, idx) => (
                      <div
                        key={idx}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold flex items-center justify-between cursor-pointer transition-colors ${
                          item.active
                            ? 'bg-[#111827] text-white shadow-2xs'
                            : 'text-[#4B5563] hover:bg-[#F3F4F6]'
                        }`}
                      >
                        <span>{item.name}</span>
                        {item.active && <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#E5E7EB] pt-4 flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-xs">
                    AD
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xs font-bold text-[#111827]">Admin Workspace</span>
                    <span className="text-[10px] text-emerald-600 font-semibold">● Twin Active</span>
                  </div>
                </div>
              </div>

              {/* Main Dashboard Panel */}
              <div className="md:col-span-9 p-6 space-y-6">
                {/* Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[#E5E7EB] pb-4">
                  <div>
                    <h3 className="text-lg font-bold text-[#111827]">AI Twin Performance Overview</h3>
                    <p className="text-xs text-slate-500">Real-time resolution metrics across all channels</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1.5 rounded-lg border border-[#E5E7EB] bg-white text-xs font-medium text-slate-700 shadow-2xs">
                      📅 Oct 1 - Oct 31, 2026
                    </span>
                  </div>
                </div>

                {/* 4 Stat Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
                  {[
                    { title: 'Total Conversations', value: '18,452', change: '+12.1%', green: true },
                    { title: 'Avg. Reply Speed', value: '0.4s', change: 'Instant', green: true },
                    { title: 'AI Resolution Rate', value: '96.4%', change: '+2.8%', green: true },
                    { title: 'Deflection Rate', value: '74.2%', change: '+1.8%', green: true },
                  ].map((card, idx) => (
                    <div key={idx} className="p-4 rounded-xl border border-[#E5E7EB] bg-white shadow-2xs">
                      <span className="text-[11px] font-medium text-slate-500 block">{card.title}</span>
                      <div className="mt-2 flex items-baseline justify-between">
                        <span className="text-xl font-bold text-[#111827]">{card.value}</span>
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-200">
                          {card.change}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Interactive Chart Preview Container */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* Line Chart */}
                  <div className="lg:col-span-2 p-4 rounded-xl border border-[#E5E7EB] bg-white shadow-2xs flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-bold text-[#111827]">Conversation Trends & Volume</span>
                      <span className="text-[10px] text-slate-500">● Resolved by AI Twin</span>
                    </div>

                    {/* SVG Trend Graph */}
                    <div className="h-32 w-full pt-2">
                      <svg viewBox="0 0 500 120" className="w-full h-full overflow-visible">
                        {/* Grid lines */}
                        <line x1="0" y1="30" x2="500" y2="30" stroke="#F3F4F6" strokeWidth="1" />
                        <line x1="0" y1="70" x2="500" y2="70" stroke="#F3F4F6" strokeWidth="1" />
                        <line x1="0" y1="110" x2="500" y2="110" stroke="#F3F4F6" strokeWidth="1" />

                        {/* Smooth Line Path */}
                        <path
                          d="M 0 90 Q 75 40, 150 60 T 300 20 T 450 40 L 500 15"
                          fill="none"
                          stroke="#111827"
                          strokeWidth="3"
                        />
                        {/* Area Fill */}
                        <path
                          d="M 0 90 Q 75 40, 150 60 T 300 20 T 450 40 L 500 15 L 500 120 L 0 120 Z"
                          fill="rgba(17,24,39,0.04)"
                        />

                        {/* Data Points */}
                        <circle cx="150" cy="60" r="4" fill="#111827" />
                        <circle cx="300" cy="20" r="4" fill="#111827" />
                        <circle cx="500" cy="15" r="5" fill="#10B981" stroke="#ffffff" strokeWidth="2" />
                      </svg>
                    </div>
                  </div>

                  {/* Active Channels */}
                  <div className="p-4 rounded-xl border border-[#E5E7EB] bg-white shadow-2xs flex flex-col justify-between">
                    <span className="text-xs font-bold text-[#111827] mb-3 block">Active Channels</span>
                    <div className="space-y-2.5">
                      {[
                        { name: 'WhatsApp Business', pct: '48%', color: 'bg-emerald-500' },
                        { name: 'Website Widget', pct: '34%', color: 'bg-slate-800' },
                        { name: 'Voice Phone Calls', pct: '18%', color: 'bg-amber-500' },
                      ].map((ch, i) => (
                        <div key={i} className="flex flex-col gap-1">
                          <div className="flex justify-between text-[11px]">
                            <span className="font-semibold text-slate-700">{ch.name}</span>
                            <span className="font-bold text-[#111827]">{ch.pct}</span>
                          </div>
                          <div className="h-2 w-full bg-[#F3F4F6] rounded-full overflow-hidden">
                            <div className={`h-full ${ch.color}`} style={{ width: ch.pct }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  Industry Categories (Expanded & Spacious)
 * ------------------------------------------------------------------ */

const IndustriesStrip = () => {
  const categories = [
    { name: 'Restaurants & Cafés', desc: 'Menu answers & table bookings' },
    { name: 'Medical Clinics', desc: 'Patient intake & appointment slots' },
    { name: 'Boutiques & Retail', desc: 'Catalog, sizing & order tracking' },
    { name: 'Real Estate', desc: 'Property inquiry qualification' },
    { name: 'Gyms & Fitness', desc: 'Trial passes & membership FAQs' },
    { name: 'Salons & Spas', desc: 'Slot scheduling & price lists' },
    { name: 'Hotels & Stays', desc: 'Amenity queries & room availability' },
    { name: 'Coaching & Tutors', desc: 'Course info & batch enrollments' },
  ];

  return (
    <section className="py-20 bg-[#F9FAFB] border-b border-[#E5E7EB] px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Built For Modern Businesses
          </span>
          <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-[#111827]">
            One platform adapted to your industry.
          </h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {categories.map((c, i) => (
            <div
              key={i}
              className="flex flex-col p-6 rounded-xl border border-[#E5E7EB] bg-white shadow-2xs hover:border-[#9CA3AF] transition-all"
            >
              <span className="text-base font-semibold text-[#111827]">{c.name}</span>
              <span className="mt-1 text-xs text-[#6B7280]">{c.desc}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  Expanded 2-Column Feature Showcases with Realistic Images
 * ------------------------------------------------------------------ */

const VoiceShowcase = () => (
  <section className="py-28 bg-white border-b border-[#E5E7EB] px-6 lg:px-12">
    <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="inline-flex items-center gap-2 rounded-md bg-[#F3F4F6] px-3 py-1 text-xs font-semibold text-[#111827]">
          <Mic className="h-3.5 w-3.5" />
          Voice Cloning Technology
        </div>
        <h2 className="mt-6 text-3xl sm:text-5xl font-bold tracking-tight text-[#111827] leading-tight">
          Talk to callers in your exact voice and accent.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
          Record 10 seconds of audio. Our speech engine captures your pitch, cadence, and tone so your twin can handle incoming phone calls natively in your voice.
        </p>

        <ul className="mt-8 space-y-3.5 text-sm text-[#374151]">
          {[
            'Clones voice in under 30 seconds with 10s sample',
            'Handles phone call interruptions naturally',
            'Full Hindi and English speech fluency',
            'Customizable call guardrails and escalation rules',
          ].map((item, idx) => (
            <li key={idx} className="flex items-center gap-3">
              <Check className="h-4 w-4 text-[#111827] flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 shadow-xl overflow-hidden">
          <img
            src="/assets/images/voice_studio.jpg"
            alt="AI Voice Cloning Studio UI"
            className="w-full h-auto rounded-xl border border-[#E5E7EB] object-cover"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

const WhatsAppShowcase = () => (
  <section className="py-28 bg-[#F9FAFB] border-b border-[#E5E7EB] px-6 lg:px-12">
    <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <Reveal className="order-2 lg:order-1">
        <div className="rounded-2xl border border-[#E5E7EB] bg-white p-3 shadow-xl overflow-hidden max-w-md mx-auto">
          <img
            src="/assets/images/whatsapp_inbox.jpg"
            alt="WhatsApp Business Automation UI"
            className="w-full h-auto rounded-xl border border-[#E5E7EB] object-cover"
          />
        </div>
      </Reveal>

      <Reveal className="order-1 lg:order-2">
        <div className="inline-flex items-center gap-2 rounded-md bg-[#E5E7EB] px-3 py-1 text-xs font-semibold text-[#111827]">
          <MessageCircle className="h-3.5 w-3.5" />
          WhatsApp Integration
        </div>
        <h2 className="mt-6 text-3xl sm:text-5xl font-bold tracking-tight text-[#111827] leading-tight">
          Instantly live on WhatsApp with zero API hassle.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
          Scan one QR code to connect your business WhatsApp account. Your twin starts replying to customer questions 24 hours a day on your existing business number.
        </p>

        <ul className="mt-8 space-y-3.5 text-sm text-[#374151]">
          {[
            'Instant connection via simple QR code scan',
            'Responds in under 1 second to every message',
            'Auto-schedules appointments and collects lead details',
            'Transfers complex cases seamlessly to human staff',
          ].map((item, idx) => (
            <li key={idx} className="flex items-center gap-3">
              <Check className="h-4 w-4 text-[#111827] flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Reveal>
    </div>
  </section>
);

const KnowledgeShowcase = () => (
  <section className="py-28 bg-white border-b border-[#E5E7EB] px-6 lg:px-12">
    <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <Reveal>
        <div className="inline-flex items-center gap-2 rounded-md bg-[#F3F4F6] px-3 py-1 text-xs font-semibold text-[#111827]">
          <Database className="h-3.5 w-3.5" />
          Knowledge Ingestion Engine
        </div>
        <h2 className="mt-6 text-3xl sm:text-5xl font-bold tracking-tight text-[#111827] leading-tight">
          Train your twin on your menus, price sheets & PDFs.
        </h2>
        <p className="mt-5 text-base sm:text-lg text-[#4B5563] leading-relaxed">
          Drop in your menu PDFs, price lists, service policies, or website links. Your AI twin ingests your knowledge in seconds and never makes up false info.
        </p>

        <ul className="mt-8 space-y-3.5 text-sm text-[#374151]">
          {[
            'Supports PDFs, Word docs, TXT, and Web scrapers',
            'Automatic vector embedding and chunking',
            'Guaranteed zero hallucination guardrails',
            'Update your price list anytime in 1-click',
          ].map((item, idx) => (
            <li key={idx} className="flex items-center gap-3">
              <Check className="h-4 w-4 text-[#111827] flex-shrink-0" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Reveal>

      <Reveal delay={0.2}>
        <div className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3 shadow-xl overflow-hidden">
          <img
            src="/assets/images/knowledge_upload.jpg"
            alt="AI Knowledge Base Upload UI"
            className="w-full h-auto rounded-xl border border-[#E5E7EB] object-cover"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 *  How It Works (Spacious 3-Step Section)
 * ------------------------------------------------------------------ */

const HowItWorks = () => {
  const steps = [
    {
      num: '01',
      title: 'Upload Knowledge & Files',
      desc: 'Drop in your menus, price lists, or paste your website URL. Your twin indexes everything immediately.',
    },
    {
      num: '02',
      title: 'Clone Voice & Set Rules',
      desc: 'Record a short audio sample to clone your voice for phone calls and set custom guardrails.',
    },
    {
      num: '03',
      title: 'Go Live Everywhere',
      desc: 'Scan a QR code for WhatsApp or add one line of code to your website. Your twin takes over 24/7.',
    },
  ];

  return (
    <section className="py-28 bg-[#F9FAFB] border-b border-[#E5E7EB] px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="text-center max-w-xl mx-auto mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Simple Implementation
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#111827]">
            3 steps from setup to live assistant.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex flex-col p-8 rounded-2xl border border-[#E5E7EB] bg-white shadow-2xs h-full">
                <span className="font-mono text-sm font-bold text-[#111827] bg-[#F3F4F6] px-3 py-1 rounded-md w-max mb-6">
                  {s.num}
                </span>
                <h3 className="text-xl font-bold text-[#111827] mb-3">{s.title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  Stats Banner
 * ------------------------------------------------------------------ */

const Stats = () => (
  <section className="py-20 bg-white border-b border-[#E5E7EB] px-6 lg:px-12">
    <div className="mx-auto max-w-6xl">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
        {[
          { v: 0.4, s: 's', label: 'Average reply speed', dec: 1 },
          { v: 24, s: '/7', label: 'Always-on availability', dec: 0 },
          { v: 2, s: ' langs', label: 'English & Hindi support', dec: 0 },
          { v: 5, s: ' min', label: 'Time to deploy', dec: 0 },
        ].map((st, i) => (
          <Reveal key={i} delay={i * 0.05}>
            <div className="text-4xl sm:text-5xl font-extrabold text-[#111827]">
              <Counter to={st.v} suffix={st.s} decimals={st.dec} />
            </div>
            <p className="mt-2 text-xs font-semibold uppercase tracking-wider text-[#6B7280]">{st.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ------------------------------------------------------------------ *
 *  Testimonials
 * ------------------------------------------------------------------ */

const Testimonials = () => {
  const reviews = [
    {
      name: 'Amit Singh',
      role: 'Owner, Spicy Basil Restaurant',
      text: 'Customers message at midnight for bookings and menu questions and get instant answers. It feels like our restaurant never closes.',
    },
    {
      name: 'Dr. Mehta',
      role: 'Founder, CareFirst Dental',
      text: 'Emergency patient intake and FAQ responses are handled automatically. My front desk staff can finally focus on in-person patients.',
    },
    {
      name: 'Riya Sharma',
      role: 'Founder, Aura Boutique',
      text: 'It answers sizing and shipping in our exact brand voice on WhatsApp. Conversion rate increased significantly.',
    },
  ];

  return (
    <section className="py-28 bg-[#F9FAFB] border-b border-[#E5E7EB] px-6 lg:px-12">
      <div className="mx-auto max-w-7xl">
        <div className="text-center max-w-xl mx-auto mb-16">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            User Reviews
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#111827]">
            Trusted by active founders.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {reviews.map((r, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="flex flex-col justify-between p-8 rounded-2xl border border-[#E5E7EB] bg-white shadow-2xs h-full">
                <div>
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-4 w-4 fill-[#111827] text-[#111827]" />
                    ))}
                  </div>
                  <p className="text-sm text-[#374151] leading-relaxed">"{r.text}"</p>
                </div>
                <div className="mt-8 border-t border-[#E5E7EB] pt-4">
                  <p className="text-sm font-bold text-[#111827]">{r.name}</p>
                  <p className="text-xs text-[#6B7280]">{r.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  Pricing Section
 * ------------------------------------------------------------------ */

const Pricing = () => {
  const [annual, setAnnual] = useState(true);
  const navigate = useNavigate();

  return (
    <section className="py-28 bg-white border-b border-[#E5E7EB] px-6 lg:px-12">
      <div className="mx-auto max-w-5xl">
        <div className="text-center max-w-xl mx-auto mb-14">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Simple Pricing
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#111827]">
            Transparent plans for growing businesses
          </h2>
          <p className="mt-3 text-sm text-[#4B5563]">
            Start free. Upgrade anytime as your message volume grows.
          </p>

          <div className="mt-6 inline-flex items-center rounded-full border border-[#E5E7EB] bg-[#F9FAFB] p-1">
            <button
              onClick={() => setAnnual(false)}
              className={`rounded-full px-5 py-1.5 text-xs font-semibold transition-all ${
                !annual ? 'bg-[#111827] text-white' : 'text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={`rounded-full px-5 py-1.5 text-xs font-semibold transition-all ${
                annual ? 'bg-[#111827] text-white' : 'text-[#6B7280] hover:text-[#111827]'
              }`}
            >
              Annual (20% OFF)
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Standard Plan */}
          <Reveal>
            <div className="flex flex-col justify-between p-8 rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] shadow-2xs h-full">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">Starter</span>
                <h3 className="mt-2 text-2xl font-bold text-[#111827]">Standard Plan</h3>
                <p className="mt-1 text-xs text-[#6B7280]">Ideal for single-location businesses.</p>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-[#111827]">
                    ₹{annual ? '1,039' : '1,299'}
                  </span>
                  <span className="text-xs text-[#6B7280]">/month</span>
                </div>

                <ul className="mt-8 space-y-3.5 border-t border-[#E5E7EB] pt-6 text-xs text-[#374151]">
                  {[
                    'Custom AI Twin trained on your documents',
                    'Website floating Chat Widget',
                    'Knowledge base ingestion (PDF/URL)',
                    'Analytics & Dashboard visibility',
                    'Standard Response Speeds',
                  ].map((feat, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <Check className="h-4 w-4 text-[#111827] flex-shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="mt-8 w-full rounded-xl border border-[#E5E7EB] bg-white py-3.5 text-xs font-bold text-[#111827] hover:bg-[#F3F4F6] transition-colors"
              >
                Start Free Trial
              </button>
            </div>
          </Reveal>

          {/* Business Pro Plan */}
          <Reveal delay={0.1}>
            <div className="flex flex-col justify-between p-8 rounded-2xl border-2 border-[#111827] bg-white shadow-xl h-full relative">
              <div className="absolute -top-3.5 right-6 rounded-full bg-[#111827] px-3.5 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                Recommended
              </div>

              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-[#111827]">Full Power</span>
                <h3 className="mt-2 text-2xl font-bold text-[#111827]">Business Pro</h3>
                <p className="mt-1 text-xs text-[#6B7280]">Multi-channel support with voice cloning.</p>

                <div className="mt-6 flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold text-[#111827]">
                    ₹{annual ? '2,399' : '2,999'}
                  </span>
                  <span className="text-xs text-[#6B7280]">/month</span>
                </div>

                <ul className="mt-8 space-y-3.5 border-t border-[#E5E7EB] pt-6 text-xs text-[#374151]">
                  {[
                    'Everything in Standard',
                    'Voice Cloning & Native Phone Call handling',
                    'Official WhatsApp Business QR Gateway',
                    'English + Hindi multi-language switching',
                    'Priority Support & Custom Rules',
                  ].map((feat, idx) => (
                    <li key={idx} className="flex items-center gap-3 font-semibold">
                      <Check className="h-4 w-4 text-[#111827] flex-shrink-0" />
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                onClick={() => navigate('/register')}
                className="mt-8 w-full rounded-xl bg-[#111827] hover:bg-[#1F2937] py-3.5 text-xs font-bold text-white shadow-sm transition-colors"
              >
                Get Started with Business Pro
              </button>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  FAQ Accordion
 * ------------------------------------------------------------------ */

const FAQS = [
  {
    q: 'Do I need any coding knowledge to set this up?',
    a: 'None at all. Simply upload your documents or website link, configure your business name, and your twin is ready. WhatsApp integration is just one QR code scan.',
  },
  {
    q: 'How does voice cloning work for phone calls?',
    a: 'You record a 10-second voice sample in our dashboard. Our speech synthesis model generates an exact voice replica to answer phone inquiries naturally.',
  },
  {
    q: 'Can my twin speak both English and Hindi?',
    a: 'Yes! It detects the language used by the customer and automatically responds in English or Hindi.',
  },
  {
    q: 'Is my business data secure?',
    a: 'Yes, your knowledge files are encrypted and isolated strictly to train your own twin. We never share or publish your business data.',
  },
];

const FAQ = () => {
  const [open, setOpen] = useState(0);

  return (
    <section className="py-28 bg-[#F9FAFB] border-b border-[#E5E7EB] px-6 lg:px-12">
      <div className="mx-auto max-w-3xl">
        <div className="text-center max-w-xl mx-auto mb-14">
          <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
            Help Center
          </span>
          <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-[#111827]">
            Frequently Asked Questions
          </h2>
        </div>

        <div className="space-y-4">
          {FAQS.map((f, i) => (
            <div
              key={i}
              className="rounded-xl border border-[#E5E7EB] bg-white p-5 transition-all"
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="flex w-full items-center justify-between text-left"
              >
                <span className="text-sm font-bold text-[#111827]">{f.q}</span>
                <span className="text-[#6B7280] font-mono text-base">{open === i ? '−' : '+'}</span>
              </button>
              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="pt-3 text-xs text-[#4B5563] leading-relaxed">{f.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  Full-Bleed Final CTA Banner
 * ------------------------------------------------------------------ */

const FinalCTA = () => {
  const navigate = useNavigate();

  return (
    <section className="py-28 bg-[#111827] text-white px-6 lg:px-12">
      <div className="mx-auto max-w-5xl text-center">
        <h2 className="text-3xl sm:text-5xl font-bold tracking-tight leading-tight">
          Ready to launch your AI Digital Twin?
        </h2>
        <p className="mt-4 max-w-xl mx-auto text-base text-[#9CA3AF]">
          Automate customer inquiries across WhatsApp, web, and phone calls in under 5 minutes.
        </p>
        <div className="mt-8 flex justify-center">
          <button
            onClick={() => navigate('/register')}
            className="flex items-center gap-2 rounded-xl bg-white hover:bg-[#F3F4F6] px-9 py-4 text-sm font-bold text-[#111827] shadow-lg transition-all"
          >
            Create Your Free Twin Now
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </section>
  );
};

/* ------------------------------------------------------------------ *
 *  Footer
 * ------------------------------------------------------------------ */

const Footer = () => {
  const navigate = useNavigate();

  return (
    <footer className="py-14 bg-white border-t border-[#E5E7EB] px-6 lg:px-12">
      <div className="mx-auto max-w-7xl flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <LogoIcon className="h-7 w-auto text-[#111827]" />
          <span className="text-sm font-bold text-[#111827]">AI Digital Twin</span>
        </div>

        <div className="flex gap-6 text-xs text-[#6B7280]">
          <button onClick={() => navigate('/pricing')} className="hover:text-[#111827] transition-colors">Pricing</button>
          <button onClick={() => navigate('/guide')} className="hover:text-[#111827] transition-colors font-medium">Guide</button>
          <button onClick={() => navigate('/login')} className="hover:text-[#111827] transition-colors">Sign in</button>
          <button onClick={() => navigate('/register')} className="hover:text-[#111827] transition-colors font-bold text-[#111827]">Get Started</button>
        </div>

        <p className="text-xs text-[#9CA3AF]">© {new Date().getFullYear()} AI Digital Twin. All rights reserved.</p>
      </div>
    </footer>
  );
};

/* ------------------------------------------------------------------ *
 *  Main Page Component
 * ------------------------------------------------------------------ */

const Home = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'AI Digital Twin — Your Business, Answering 24/7';
    return () => {
      document.title = prev;
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative min-h-screen bg-white text-[#111827] font-sans antialiased selection:bg-[#111827] selection:text-white">
        <LandingNavbar />
        <main>
          <Hero />
          <IndustriesStrip />
          <VoiceShowcase />
          <WhatsAppShowcase />
          <KnowledgeShowcase />
          <HowItWorks />
          <Stats />
          <Testimonials />
          <Pricing />
          <FAQ />
          <FinalCTA />
        </main>
        <Footer />
      </div>
    </MotionConfig>
  );
};

export default Home;
